import React, { useState, useEffect } from 'react';
import { ApiClient } from '../../api/client';
import type { AuditLogEntry, Tenant } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { downloadAuditLogPDF, downloadAuditLogXLSX } from '../../utils/export';
import { ActivityAnalyticsView } from './ActivityAnalyticsView';
import {
  History, RefreshCw, Search, ChevronDown, ChevronRight,
  Download, FileSpreadsheet, LogIn, LogOut, UserCheck,
  PlusCircle, Edit3, Trash2, Paperclip, ArrowUpRight, ArrowDownLeft,
  Layers, Sliders, Users, Filter, Eye, ShieldAlert, BarChart3
} from 'lucide-react';

export const AuditLogView: React.FC = () => {
  const { user, isImpersonating, tenant } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin' || user?.role === 'tenant_admin' || user?.granular_role === 'superadmin' || user?.granular_role === 'tenant_admin';
  const isOrgAdmin = user?.role === 'org_admin' || user?.granular_role === 'org_admin';
  const isAuditor = user?.role === 'auditor' || user?.granular_role === 'auditor';
  const canViewAudit = isSuperAdmin || isOrgAdmin || isAuditor;

  const [activeTab, setActiveTab] = useState<'analytics' | 'logs'>(isSuperAdmin ? 'analytics' : 'logs');
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [tenantFilter, setTenantFilter] = useState<string>(() => {
    return isImpersonating && tenant?.id ? String(tenant.id) : 'all';
  });
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [isExporting, setIsExporting] = useState<'xlsx' | 'pdf' | null>(null);

  useEffect(() => {
    if (isImpersonating && tenant?.id) {
      setTenantFilter(String(tenant.id));
    }
  }, [isImpersonating, tenant?.id]);

  const loadLogs = async () => {
    setLoading(true);
    const params: any = { limit: 250 };
    if (tenantFilter !== 'all') {
      params.tenant_id = parseInt(tenantFilter);
    }
    const res = await ApiClient.getAuditLogs(params);
    if (res.ok && res.data) {
      setLogs(res.data);
    }
    setLoading(false);
  };

  const loadTenants = async () => {
    if (isSuperAdmin) {
      const res = await ApiClient.getTenants();
      if (res.ok && res.data) {
        setTenants(res.data);
      }
    }
  };

  useEffect(() => {
    if (!canViewAudit) return;
    loadLogs();
  }, [tenantFilter, canViewAudit]);

  useEffect(() => {
    loadTenants();
  }, [isSuperAdmin]);

  // Distinct Color, Icon & Label Badge for Each Operation Type
  const getActionBadge = (action: string) => {
    const a = (action || '').toLowerCase();

    // 1. Megszemélyesítés
    if (a.includes('impersonate')) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-amber-50 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-800 shadow-xs">
          <UserCheck className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
          <span>MEGSZEMÉLYESÍTÉS</span>
        </span>
      );
    }

    // 2. Bejelentkezés / Kijelentkezés
    if (a === 'login' || a.includes('auth_login')) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-blue-50 dark:bg-blue-950/60 text-blue-800 dark:text-blue-300 border border-blue-300 dark:border-blue-800 shadow-xs">
          <LogIn className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 shrink-0" />
          <span>BEJELENTKEZÉS</span>
        </span>
      );
    }
    if (a === 'logout') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-700 shadow-xs">
          <LogOut className="w-3.5 h-3.5 text-slate-500 shrink-0" />
          <span>KIJELENTKEZÉS</span>
        </span>
      );
    }

    // 3. Fájl / Melléklet műveletek
    if (a === 'attachment_download' || (a.includes('attachment') && a.includes('download'))) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-blue-50 dark:bg-blue-950/60 text-blue-800 dark:text-blue-300 border border-blue-300 dark:border-blue-800 shadow-xs">
          <Download className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 shrink-0" />
          <span>FÁJL LETÖLTÉS</span>
        </span>
      );
    }
    if (a === 'attachment_preview' || (a.includes('attachment') && a.includes('preview'))) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-cyan-50 dark:bg-cyan-950/60 text-cyan-800 dark:text-cyan-300 border border-cyan-300 dark:border-cyan-800 shadow-xs">
          <Eye className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400 shrink-0" />
          <span>FÁJL ELŐNÉZET</span>
        </span>
      );
    }
    if (a === 'attachment_delete' || (a.includes('attachment') && a.includes('delete'))) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-rose-50 dark:bg-rose-950/60 text-rose-800 dark:text-rose-300 border border-rose-300 dark:border-rose-800 shadow-xs">
          <Trash2 className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400 shrink-0" />
          <span>FÁJL TÖRLÉS</span>
        </span>
      );
    }
    if (a === 'attachment_upload' || a === 'attachment_upload_bulk') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-purple-50 dark:bg-purple-950/60 text-purple-800 dark:text-purple-300 border border-purple-300 dark:border-purple-800 shadow-xs">
          <Paperclip className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400 shrink-0" />
          <span>FÁJL FELTÖLTÉS</span>
        </span>
      );
    }
    if (a === 'attachment_rename') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-amber-50 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-800 shadow-xs">
          <Edit3 className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
          <span>FÁJL ÁTNEVEZÉS</span>
        </span>
      );
    }
    if (a === 'attachment_move') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-800 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-800 shadow-xs">
          <ArrowUpRight className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400 shrink-0" />
          <span>FÁJL ÁTHELYEZÉS</span>
        </span>
      );
    }
    if (a === 'attachment_copy') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-teal-50 dark:bg-teal-950/60 text-teal-800 dark:text-teal-300 border border-teal-300 dark:border-teal-800 shadow-xs">
          <PlusCircle className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400 shrink-0" />
          <span>FÁJL MÁSOLÁS</span>
        </span>
      );
    }
    if (a === 'attachment_export_zip') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800 shadow-xs">
          <Download className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
          <span>FÁJL ZIP EXPORT</span>
        </span>
      );
    }

    // 4. Általános törlések
    if (a.includes('delete') || a.includes('remove') || a.includes('truncate')) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-rose-50 dark:bg-rose-950/60 text-rose-800 dark:text-rose-300 border border-rose-300 dark:border-rose-800 shadow-xs">
          <Trash2 className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400 shrink-0" />
          <span>TÖRLÉS</span>
        </span>
      );
    }

    // 5. Létrehozás / Új bejegyzés
    if (a.includes('create') || a.includes('insert') || a.includes('add')) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800 shadow-xs">
          <PlusCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
          <span>ÚJ LÉTREHOZÁS</span>
        </span>
      );
    }

    // 6. Módosítás / Mentés
    if (a.includes('update') || a.includes('edit') || a.includes('save') || a.includes('patch')) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-sky-50 dark:bg-sky-950/60 text-sky-800 dark:text-sky-300 border border-sky-300 dark:border-sky-800 shadow-xs">
          <Edit3 className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400 shrink-0" />
          <span>MÓDOSÍTÁS</span>
        </span>
      );
    }

    // 7. Egyéb csatolmány művelet
    if (a.includes('attachment') || a.includes('upload')) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-purple-50 dark:bg-purple-950/60 text-purple-800 dark:text-purple-300 border border-purple-300 dark:border-purple-800 shadow-xs">
          <Paperclip className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400 shrink-0" />
          <span>FÁJLFELTÖLTÉS</span>
        </span>
      );
    }

    // 7. Szinkronizáció Push / Pull
    if (a.includes('push')) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-lime-50 dark:bg-lime-950/60 text-lime-800 dark:text-lime-300 border border-lime-300 dark:border-lime-800 shadow-xs">
          <ArrowUpRight className="w-3.5 h-3.5 text-lime-600 dark:text-lime-400 shrink-0" />
          <span>SZINKRON (PUSH)</span>
        </span>
      );
    }
    if (a.includes('pull') || a.includes('sync')) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-teal-50 dark:bg-teal-950/60 text-teal-800 dark:text-teal-300 border border-teal-300 dark:border-teal-800 shadow-xs">
          <ArrowDownLeft className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400 shrink-0" />
          <span>SZINKRON (PULL)</span>
        </span>
      );
    }

    // 8. Staging & Weboldal átemelés
    if (a.includes('scrape') || a.includes('staging')) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-800 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-800 shadow-xs">
          <Layers className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400 shrink-0" />
          <span>WEB ÁTEMELÉS / STAGING</span>
        </span>
      );
    }

    // 9. Oldalak és Mezők konfigurációja
    if (a.includes('page') || a.includes('field')) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-orange-50 dark:bg-orange-950/60 text-orange-800 dark:text-orange-300 border border-orange-300 dark:border-orange-800 shadow-xs">
          <Sliders className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400 shrink-0" />
          <span>STRUKTÚRA DEFINÍCIÓ</span>
        </span>
      );
    }

    // 10. Felhasználókezelés
    if (a.includes('user')) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-violet-50 dark:bg-violet-950/60 text-violet-800 dark:text-violet-300 border border-violet-300 dark:border-violet-800 shadow-xs">
          <Users className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400 shrink-0" />
          <span>FELHASZNÁLÓKEZELÉS</span>
        </span>
      );
    }

    // 11. Export
    if (a.includes('export')) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700 shadow-xs">
          <Download className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
          <span>EXPORTÁLÁS</span>
        </span>
      );
    }

    // Alapértelmezett kártya
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-700">
        <span>{action}</span>
      </span>
    );
  };

  // Filter application
  const filtered = logs.filter((l) => {
    // 1. Text Search
    if (search.trim()) {
      const q = search.toLowerCase();
      const matchAction = l.action && l.action.toLowerCase().includes(q);
      const matchEntity = l.entity && l.entity.toLowerCase().includes(q);
      const matchUser = (l.user_email && l.user_email.toLowerCase().includes(q)) || (l.user_name && l.user_name.toLowerCase().includes(q));
      const matchTenant = l.tenant_name && l.tenant_name.toLowerCase().includes(q);
      const matchDetail = l.detail && (typeof l.detail === 'string' ? l.detail.toLowerCase().includes(q) : JSON.stringify(l.detail).toLowerCase().includes(q));

      if (!matchAction && !matchEntity && !matchUser && !matchTenant && !matchDetail) {
        return false;
      }
    }

    // Nem-superadmin felhasználók számára sosem jelenhetnek meg superadmin műveletek
    if (!isSuperAdmin) {
      if (l.user_role === 'superadmin' || l.user_role === 'tenant_admin') return false;
      const act = (l.action || '').toLowerCase();
      if (
        act === 'impersonate' ||
        act.startsWith('sandbox_') ||
        act.startsWith('maintenance_') ||
        act.startsWith('lockdown_') ||
        act.startsWith('dev_features_') ||
        act.startsWith('system_branding_') ||
        act.startsWith('backup_')
      ) {
        return false;
      }
    }

    // 2. Category Filter
    if (categoryFilter !== 'all') {
      const act = (l.action || '').toLowerCase();
      if (categoryFilter === 'auth' && !act.includes('login') && !act.includes('logout')) return false;
      if (categoryFilter === 'records' && !act.includes('record')) return false;
      if (categoryFilter === 'deletes' && !act.includes('delete') && !act.includes('remove')) return false;
      if (categoryFilter === 'attachments' && !act.includes('attachment') && !act.includes('upload') && !act.includes('download') && !act.includes('preview') && !act.includes('file')) return false;
      if (categoryFilter === 'sync' && !act.includes('sync') && !act.includes('push') && !act.includes('pull')) return false;
      if (categoryFilter === 'impersonate' && !act.includes('impersonate')) return false;
      if (categoryFilter === 'structure' && !act.includes('page') && !act.includes('field')) return false;
      if (categoryFilter === 'users' && !act.includes('user')) return false;
    }

    return true;
  });

  const handleExportXLSX = async () => {
    setIsExporting('xlsx');
    try {
      await downloadAuditLogXLSX({
        search: search.trim() || undefined,
        action: categoryFilter !== 'all' ? categoryFilter : undefined,
        tenant_id: tenantFilter !== 'all' ? parseInt(tenantFilter) : undefined,
      });
    } catch (err: any) {
      alert('Hiba az Excel exportálás során: ' + (err.message || 'Ismeretlen hiba'));
    } finally {
      setIsExporting(null);
    }
  };

  const handleExportPDF = async () => {
    setIsExporting('pdf');
    try {
      const descParts = [];
      if (search) descParts.push(`Keresés: "${search}"`);
      if (categoryFilter !== 'all') descParts.push(`Kategória: ${categoryFilter}`);
      if (tenantFilter !== 'all') descParts.push(`Intézmény ID: ${tenantFilter}`);
      await downloadAuditLogPDF(filtered, descParts.join(' | '));
    } catch (err: any) {
      alert('Hiba a PDF exportálás során: ' + (err.message || 'Ismeretlen hiba'));
    } finally {
      setIsExporting(null);
    }
  };

  if (!canViewAudit) {
    return (
      <div className="p-8 text-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-xl mx-auto space-y-4 shadow-xs mt-12">
        <ShieldAlert className="w-12 h-12 text-rose-600 dark:text-rose-400 mx-auto" />
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">Hozzáférés Megtagadva</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          A műveleti napló (Audit Trail) megtekintéséhez auditori vagy adminisztrátori jogosultság szükséges. Az Ön fiókja nem rendelkezik ezzel a jogosultsággal.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Title and Export Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-5 border-b border-slate-200 dark:border-slate-800">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white flex items-center gap-2.5">
            <History className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
            <span>Műveleti Napló (Audit Trail)</span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Változatlan, időbélyeggel és felhasználói adatokkal hitelesített eseménynapló biztonsági ellenőrzésekhez
          </p>
        </div>

        <div className="flex items-center flex-wrap gap-2.5">
          {/* XLSX Export Button */}
          <button
            onClick={handleExportXLSX}
            disabled={isExporting !== null}
            className="flex items-center gap-2 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-semibold shadow-xs transition cursor-pointer disabled:opacity-50"
            title="Szűrt lista letöltése Excel (.xlsx) formátumban"
          >
            {isExporting === 'xlsx' ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4" />
            )}
            <span>Excel (.xlsx)</span>
          </button>

          {/* PDF Export Button */}
          <button
            onClick={handleExportPDF}
            disabled={isExporting !== null}
            className="flex items-center gap-2 px-3.5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-semibold shadow-xs transition cursor-pointer disabled:opacity-50"
            title="Szűrt lista letöltése nyomtatható PDF dokumentumként"
          >
            {isExporting === 'pdf' ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            <span>PDF (.pdf)</span>
          </button>

          {/* Refresh Button */}
          <button
            onClick={loadLogs}
            disabled={loading}
            className="flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-semibold transition cursor-pointer shadow-xs"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Frissítés</span>
          </button>
        </div>
      </div>

      {/* Mode Switcher Tabs for Superadmin */}
      {isSuperAdmin && (
        <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
          <button
            onClick={() => setActiveTab('analytics')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs transition cursor-pointer ${
              activeTab === 'analytics'
                ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/20'
                : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            <span>Műveleti Elemzés & Kattintási Statisztika</span>
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs transition cursor-pointer ${
              activeTab === 'logs'
                ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/20'
                : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800'
            }`}
          >
            <History className="w-4 h-4" />
            <span>Részletes Eseménynapló (Audit Trail)</span>
          </button>
        </div>
      )}

      {activeTab === 'analytics' ? (
        <ActivityAnalyticsView tenants={tenants} />
      ) : (
        <>
          {/* Filter and Search Bar */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 bg-white dark:bg-slate-900/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
            {/* Search */}
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            placeholder="Keresés művelet, felhasználó, entitás vagy adat szerint..."
            className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-200 text-xs placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-xs"
          />
        </div>

        {/* Category Filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400 shrink-0 hidden sm:block" />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="w-full py-2 px-3 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-200 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-xs"
          >
            <option value="all">Minden művelet kategória</option>
            <option value="auth">🔑 Be- és Kijelentkezések</option>
            <option value="records">📝 Rekord Módosítások & Újak</option>
            <option value="deletes">🗑 Törlések</option>
            <option value="attachments">📎 Fájlműveletek (Letöltés, Feltöltés, Törlés)</option>
            <option value="sync">🔄 Szinkronizációk (Push / Pull)</option>
            {isSuperAdmin && <option value="impersonate">🎭 Megszemélyesítések</option>}
            <option value="structure">⚙️ Oldal & Mező Definíciók</option>
            <option value="users">👥 Felhasználó Adminisztráció</option>
          </select>
        </div>

        {/* Tenant Filter (Superadmin only) */}
        {isSuperAdmin && (
          <div className="flex items-center gap-2">
            <select
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
              className="w-full py-2 px-3 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-200 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-xs"
            >
              <option value="all">Minden intézmény naplója</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} (/{t.slug})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Filter Status Summary */}
      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 px-1">
        <div>
          Találatok száma: <span className="font-bold text-slate-900 dark:text-white">{filtered.length}</span> tétel
          {filtered.length !== logs.length && (
            <span className="ml-1 text-slate-400 dark:text-slate-500">(összes betöltött: {logs.length})</span>
          )}
        </div>
        {(search || categoryFilter !== 'all' || tenantFilter !== 'all') && (
          <button
            onClick={() => { setSearch(''); setCategoryFilter('all'); setTenantFilter('all'); }}
            className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium cursor-pointer"
          >
            Szűrők törlése
          </button>
        )}
      </div>

      {/* Log Table */}
      <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 dark:bg-slate-950/70 border-b border-slate-200 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3.5 w-10"></th>
                <th className="px-4 py-3.5 font-semibold whitespace-nowrap">Időpont</th>
                <th className="px-4 py-3.5 font-semibold">Művelet Típusa</th>
                <th className="px-4 py-3.5 font-semibold">Entitás</th>
                <th className="px-4 py-3.5 font-semibold">Felhasználó</th>
                {isSuperAdmin && <th className="px-4 py-3.5 font-semibold">Intézmény</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800/60">
              {loading ? (
                <tr>
                  <td colSpan={isSuperAdmin ? 6 : 5} className="py-12 text-center text-slate-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-indigo-500 mb-2" />
                    Audit napló betöltése...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={isSuperAdmin ? 6 : 5} className="py-10 text-center text-slate-500">
                    Nincs a szűrésnek megfelelő audit esemény.
                  </td>
                </tr>
              ) : (
                filtered.map((log) => {
                  const isExpanded = expandedId === log.id;
                  let parsedDetail: any = null;
                  try {
                    parsedDetail = typeof log.detail === 'string' ? JSON.parse(log.detail) : log.detail;
                  } catch {
                    parsedDetail = log.detail;
                  }

                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        onClick={() => setExpandedId(isExpanded ? null : log.id)}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors cursor-pointer group"
                      >
                        <td className="px-4 py-3 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200">
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-slate-600 dark:text-slate-400 whitespace-nowrap">
                          {log.created_at ? new Date(log.created_at).toLocaleString('hu-HU') : '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {getActionBadge(log.action)}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-900 dark:text-slate-200 font-medium">
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1.5">
                              <span className="font-semibold">
                                {log.entity === 'attachment' ? 'Melléklet' : (log.entity || 'Rendszer')}
                              </span>
                              {log.entity_id ? (
                                <span className="text-[11px] text-slate-400 font-mono">#{log.entity_id}</span>
                              ) : null}
                            </div>
                            {parsedDetail?.filename && (
                              <span className="text-[11px] text-indigo-600 dark:text-indigo-400 font-normal truncate max-w-xs flex items-center gap-1" title={parsedDetail.filename}>
                                📄 {parsedDetail.filename}
                              </span>
                            )}
                            {!parsedDetail?.filename && parsedDetail?.label && (
                              <span className="text-[11px] text-slate-500 dark:text-slate-400 font-normal truncate max-w-xs" title={parsedDetail.label}>
                                {parsedDetail.label}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-700 dark:text-slate-300">
                          {log.user_name || log.user_email || (log.user_id ? `User #${log.user_id}` : 'Rendszer')}
                        </td>
                        {isSuperAdmin && (
                          <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                            {log.tenant_name || '–'}
                          </td>
                        )}
                      </tr>
                      {isExpanded && (
                        <tr className="bg-slate-50/80 dark:bg-slate-950/60 border-b border-slate-200 dark:border-slate-800">
                          <td colSpan={isSuperAdmin ? 6 : 5} className="px-8 py-4">
                            <div className="text-xs font-mono text-slate-800 dark:text-slate-300 bg-white dark:bg-slate-900/90 p-4 rounded-xl border border-slate-200 dark:border-slate-800 overflow-x-auto shadow-xs space-y-2">
                              <div className="flex items-center justify-between text-[11px] text-indigo-600 dark:text-indigo-400 font-sans font-bold uppercase tracking-wider pb-1.5 border-b border-slate-100 dark:border-slate-800">
                                <span>Részletes Paraméterek & Payload (JSON)</span>
                                <span className="text-[10px] text-slate-400 font-normal">Azonosító: #{log.id}</span>
                              </div>
                              <pre className="text-[11px] leading-relaxed overflow-x-auto">
                                {JSON.stringify(parsedDetail, null, 2)}
                              </pre>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )}
</div>
  );
};
